"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateVolatilitySmileForDomain = exports.getOptionInstrumentParts = exports.getTickersForBook = exports.getExpiryInstruments = void 0;
const types_1 = require("../types");
/**
 * Finds all instruments for every strike that exist in a book. TODO explain more
 * @param {string[]} positionsInstruments
 * @param allPossibleInstrumentsStrings
 */
const getExpiryInstruments = (positionsInstruments, allPossibleInstrumentsStrings) => {
    const options = allPossibleInstrumentsStrings.filter((i) => i.split("-").length === 4);
    const futures = positionsInstruments.filter((i) => i.split("-").length === 2);
    let res = [];
    for (const instrument of positionsInstruments) {
        const instrumentParts = instrument.split("-");
        const bookName = [instrumentParts[0], instrumentParts[1]].join("-");
        const allBookInstruments = options
            .filter((i) => i.includes(bookName))
            .filter((i) => i.split("-")[3] === instrumentParts[3]);
        res = [...res, ...allBookInstruments];
    }
    return [...res, ...futures];
};
exports.getExpiryInstruments = getExpiryInstruments;
const getTickersForBook = (tickers, instrument) => {
    const currentBookInstruments = (0, exports.getExpiryInstruments)([instrument], Object.keys(tickers));
    return currentBookInstruments.map((i) => tickers[i]);
};
exports.getTickersForBook = getTickersForBook;
const getOptionInstrumentParts = (instrument) => {
    const parts = instrument.split("-");
    return {
        currency: parts[0],
        date: parts[1],
        strike: parseFloat(parts[2]),
        direction: parts[3] === "C" ? types_1.OptionType.CALL : types_1.OptionType.PUT,
    };
};
exports.getOptionInstrumentParts = getOptionInstrumentParts;
const IMPLIED_VOL_MAX_EXTRAPOLATION_PER_MONEYNESS = 0.1;
const calculateVolatilitySmileForDomain = (domain, tickers, position, implVolType, t, minTExp) => {
    const timeToExpiry = Math.max(t, minTExp);
    if (implVolType === types_1.ImpliedVolatilityType.STICKY_STRIKE) {
        const iv = tickers[position.instrument].volatility / 100;
        return new Array(domain.length).fill(iv);
    }
    if (domain.length === 1) {
        return [tickers[position.instrument].volatility / 100];
    }
    const currentBookTickers = (0, exports.getTickersForBook)(tickers, position.instrument);
    // Index or mark price?
    const expiryData = getOrderBookMoneynessMap(currentBookTickers, tickers[position.instrument].indexPrice, timeToExpiry);
    // const posSmile = buildIVCurveForPositionInterpolate(domain, expiryData, getOptionInstrumentParts(position.instrument).strike, t)
    const posSmile = buildIVCurveForPositionInterpolate2(domain, expiryData, position, t, IMPLIED_VOL_MAX_EXTRAPOLATION_PER_MONEYNESS);
    return posSmile;
};
exports.calculateVolatilitySmileForDomain = calculateVolatilitySmileForDomain;
const buildIVCurveForPositionInterpolate2 = (domain, expiryStrikeVolatilityData, position, t, maxExtrapolationPerMoneyness) => {
    let res = [];
    const { strike } = (0, exports.getOptionInstrumentParts)(position.instrument);
    // can be taken the lowest and the highest strike data, no need probably for this
    const { maxMoneyness, minMoneyness } = expiryStrikeVolatilityData.reduce((memo, current) => {
        return {
            maxMoneyness: Math.max(current.moneyness, memo.maxMoneyness),
            minMoneyness: Math.min(current.moneyness, memo.minMoneyness),
        };
    }, {
        maxMoneyness: Number.MIN_SAFE_INTEGER,
        minMoneyness: Number.MAX_SAFE_INTEGER,
    });
    for (const x of domain) {
        const moneyness = calculateMoneyness(x, strike, t);
        if (moneyness >= maxMoneyness) {
            const underlyingPriceIVData = getExtrapolatedIVStrikeData(x, strike, expiryStrikeVolatilityData[0], expiryStrikeVolatilityData[1], t, maxExtrapolationPerMoneyness);
            res.push(underlyingPriceIVData.markIV);
        }
        else if (moneyness <= minMoneyness) {
            const underlyingPriceIVData = getExtrapolatedIVStrikeData(x, strike, expiryStrikeVolatilityData[expiryStrikeVolatilityData.length - 1], expiryStrikeVolatilityData[expiryStrikeVolatilityData.length - 2], t, maxExtrapolationPerMoneyness);
            res.push(underlyingPriceIVData.markIV);
        }
        else {
            for (let i = 0; i < expiryStrikeVolatilityData.length - 1; i++) {
                if ((expiryStrikeVolatilityData[i].moneyness > moneyness &&
                    expiryStrikeVolatilityData[i + 1].moneyness < moneyness) ||
                    (expiryStrikeVolatilityData[i].moneyness < moneyness &&
                        expiryStrikeVolatilityData[i + 1].moneyness > moneyness)) {
                    const iv = interpolateIVBetweenStrikes(expiryStrikeVolatilityData[i], expiryStrikeVolatilityData[i + 1], moneyness);
                    res.push(iv);
                }
                else if (expiryStrikeVolatilityData[i].moneyness === moneyness) {
                    res.push(expiryStrikeVolatilityData[i].markIV);
                }
                else {
                    if (i === expiryStrikeVolatilityData.length - 2 &&
                        expiryStrikeVolatilityData[i].moneyness === moneyness) {
                        res.push(expiryStrikeVolatilityData[i].markIV);
                    }
                }
            }
        }
    }
    return res;
};
const interpolateIVBetweenStrikes = (below, above, currentMoneyness) => {
    return (Math.pow(((Math.pow(below.markIV, 2) * (above.moneyness - currentMoneyness) +
        Math.pow(above.markIV, 2) * (currentMoneyness - below.moneyness)) /
        (above.moneyness - below.moneyness)), 0.5));
};
const getOrderBookMoneynessMap = (tickers, underlyingPrice, t) => {
    let existingStrikesMoneyness = tickers
        .map((ticker) => {
        const instrumentParts = (0, exports.getOptionInstrumentParts)(ticker.instrument);
        const moneyness = calculateMoneyness(underlyingPrice, instrumentParts.strike, t);
        return {
            strike: instrumentParts.strike,
            moneyness,
            markIV: ticker.volatility / 100,
        };
    })
        .sort((a, b) => a.strike - b.strike);
    return existingStrikesMoneyness;
};
const calculateMoneyness = (x, strike, t) => {
    return Math.log(x / strike) / Math.sqrt(t);
};
/**
 *
 * TODO doc
 *
 * @param newStrike
 * @param underlyingPrice
 * @param closer
 * @param further
 * @param t
 * @returns
 */
const getExtrapolatedIVStrikeData = (x, strike, closer, further, t, maxExtrapolationPerMoneyness) => {
    const moneyness = calculateMoneyness(x, strike, t);
    let xx1 = closer.markIV *
        (1 + maxExtrapolationPerMoneyness * Math.min(2, Math.max(moneyness, 0.5)));
    let xx2 = Math.pow(further.markIV, 2) +
        ((moneyness - further.moneyness) / (closer.moneyness - further.moneyness)) *
            (Math.pow(closer.markIV, 2) - Math.pow(further.markIV, 2));
    let xx3 = isNaN(Math.sqrt(xx2)) ? Number.MAX_SAFE_INTEGER : Math.sqrt(xx2);
    let xx4 = Math.min(xx1, xx3);
    let xx5 = closer.markIV *
        (1 -
            maxExtrapolationPerMoneyness *
                Math.min(2, Math.max(moneyness - closer.moneyness, 0.5)));
    let markIV = Math.max(xx4, xx5);
    return {
        strike: x,
        moneyness,
        markIV,
    };
};
